/**
 * AG Post Editor, frontend
 * Lightweight router: login → list/new → editor (create or edit).
 * Reads config from window.AG_POST_EDITOR_CONFIG (endpoint, rootSelector).
 */
(function () {
  'use strict';

  // ── Config & state ───────────────────────────────────────
  const cfg = window.AG_POST_EDITOR_CONFIG || {};
  const endpoint = cfg.endpoint || '';
  const bridgeLabel = cfg.bridgeLabel || '';   // p.ej. "Enviar a Assassin Goods UK", vacío desactiva el botón del puente
  const bridgeTargetShort = /\bUK\b/i.test(bridgeLabel) ? 'UK' : 'EU';  // etiqueta corta para el distintivo "En UK/EU"
  const root = document.querySelector(cfg.rootSelector || '[data-ag-pe-root]');
  if (!root) return;

  const state = {
    password: sessionStorage.getItem('agpe_password') || '',
    view: 'login',           // login | list | editor
    blogs: [],
    articles: [],
    currentArticle: null,    // null = create; otherwise the article being edited
    quill: null,
    selectedProducts: [],    // [{id, title, imageUrl}]
    featuredImage: { url: '', altText: '' },
    authorAvatarUrl: '',
    bridgeSelection: new Set(),  // ids de entradas marcadas para el envío por lotes a la tienda asociada
  };

  // ── API helper ───────────────────────────────────────────
  async function api(action, payload, timeoutMs) {
    if (!endpoint) throw new Error('No endpoint configured.');
    const body = Object.assign({ action: action, password: state.password }, payload || {});
    const controller = new AbortController();
    const t = timeoutMs || 45000;
    const timer = setTimeout(() => controller.abort(), t);
    let resp;
    try {
      resp = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify(body),
        signal: controller.signal
      });
    } catch (e) {
      clearTimeout(timer);
      if (e.name === 'AbortError') throw new Error('La solicitud ha caducado tras ' + (t / 1000) + 's. Apps Script puede estar frío, inténtalo de nuevo.');
      throw e;
    }
    clearTimeout(timer);
    let data;
    try { data = await resp.json(); } catch (e) { data = { error: 'bad_response' }; }
    if (!resp.ok || data.error) {
      const msg = data.error === 'auth' ? 'Contraseña incorrecta' : (data.error || 'La solicitud ha fallado');
      throw new Error(msg);
    }
    return data;
  }

  // ── Toasts ───────────────────────────────────────────────
  function toast(msg, kind) {
    const el = document.createElement('div');
    el.className = 'ag-pe-toast' + (kind ? ' ag-pe-toast--' + kind : '');
    // Errors get a richer card: heading + body + dismiss button. Other
    // kinds (success / info) stay as the simple single-line slide-in.
    if (kind === 'error') {
      el.classList.add('ag-pe-toast--rich');
      const safe = String(msg).replace(/[&<>"]/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;'}[c]));
      el.innerHTML = `
        <div class="ag-pe-toast__head">
          <strong>No se pudo guardar</strong>
          <button type="button" class="ag-pe-toast__close" aria-label="Descartar">×</button>
        </div>
        <p class="ag-pe-toast__body">${safe}</p>
      `;
      el.querySelector('.ag-pe-toast__close').addEventListener('click', () => {
        el.classList.remove('is-visible');
        setTimeout(() => el.remove(), 300);
      });
    } else {
      el.textContent = msg;
    }
    document.body.appendChild(el);
    requestAnimationFrame(() => el.classList.add('is-visible'));
    // Errors stay until dismissed; success/info auto-hide.
    if (kind !== 'error') {
      setTimeout(() => {
        el.classList.remove('is-visible');
        setTimeout(() => el.remove(), 300);
      }, 3200);
    }
  }

  // Turn raw backend / network errors into something the editor user
  // can actually act on. Falls back to the raw message if nothing
  // matches so we never swallow real diagnostic info.
  function friendlyError(err) {
    const raw = (err && err.message) ? String(err.message) : String(err || 'Error desconocido');

    // GraphQL errors come back as "GraphQL errors: [{...}]", parse out
    // the message + field path so we can map them to plain English.
    if (raw.indexOf('GraphQL errors:') === 0) {
      let arr;
      try { arr = JSON.parse(raw.slice('GraphQL errors:'.length).trim()); } catch (e) { arr = null; }
      if (Array.isArray(arr) && arr.length) {
        const first = arr[0] || {};
        const path = ((first.extensions && first.extensions.value && Object.keys(first.extensions.value)) || []);
        const errPath = (first.problems && first.problems[0] && first.problems[0].path && first.problems[0].path.join('.')) ||
                        (first.path && first.path.join && first.path.join('.')) ||
                        (first.problems && first.problems[0] && first.problems[0].path) ||
                        '';
        const fieldMap = {
          'author': 'Nombre del autor',
          'title': 'Título de la entrada',
          'body': 'Cuerpo de la entrada',
          'handle': 'Handle de URL',
          'blogId': 'Blog',
          'summary': 'Resumen',
          'publishedAt': 'Fecha de publicación',
          'image': 'Imagen destacada',
          'tags': 'Etiquetas'
        };
        const friendlyField = fieldMap[errPath] || errPath;
        const m = String(first.message || '');
        if (m.indexOf('Expected value to not be null') !== -1) {
          return `"${friendlyField || 'Un campo obligatorio'}" no puede estar vacío. Rellénalo e inténtalo de nuevo.`;
        }
        if (m.indexOf('is invalid') !== -1) {
          return `"${friendlyField || 'Un campo'}" tiene un valor no válido. Revisa el formato e inténtalo de nuevo.`;
        }
        if (m.indexOf('too long') !== -1) {
          return `"${friendlyField || 'Un campo'}" es demasiado largo. Acórtalo e inténtalo de nuevo.`;
        }
        if (m.indexOf('has already been taken') !== -1) {
          return 'Ya existe una entrada con este handle de URL. Elige un handle diferente.';
        }
        // Fallback: clean the raw GraphQL noise off the front
        return friendlyField
          ? `El backend rechazó "${friendlyField}": ${m}`
          : m;
      }
    }

    // Shopify userErrors come through as JSON-stringified arrays
    if (raw.indexOf('userErrors') !== -1 || raw.startsWith('[{')) {
      try {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr) && arr[0] && arr[0].message) {
          const f = (arr[0].field && arr[0].field.join && arr[0].field.join('.')) || '';
          return f ? `${arr[0].message} (${f})` : arr[0].message;
        }
      } catch (e) {}
    }

    // Network-y messages
    if (/Failed to fetch|NetworkError|net::|ERR_INTERNET_DISCONNECTED/i.test(raw)) {
      return 'Se ha perdido la conexión con el backend del editor. Comprueba tu conexión a internet e inténtalo de nuevo.';
    }
    if (/timed? ?out|AbortError/i.test(raw)) {
      return 'La solicitud ha caducado. Shopify puede ir lento, espera un momento e inténtalo de nuevo.';
    }
    if (/401|auth|unauthori[sz]ed/i.test(raw)) {
      return 'Tu sesión del editor ha caducado. Recarga la página e inicia sesión de nuevo.';
    }
    if (/405/.test(raw)) {
      return 'El backend rechazó la solicitud (405). Puede que haya que volver a desplegar la app web de Apps Script.';
    }

    // Last resort, short, generic
    return raw.length > 200 ? raw.slice(0, 200) + '…' : raw;
  }

  // ── Login view ───────────────────────────────────────────
  function renderLogin(errorMsg) {
    root.innerHTML = `
      <div class="ag-pe-login">
        <h2 class="ag-pe-login__title">Acceso del personal</h2>
        <form class="ag-pe-login__form" id="ag-pe-login-form">
          <input type="password" class="ag-pe-input" id="ag-pe-pw" placeholder="Contraseña" required autofocus>
          <button type="submit" class="ag-pe-btn">Entrar</button>
          ${errorMsg ? `<p class="ag-pe-login__error">${escapeHtml(errorMsg)}</p>` : ''}
        </form>
      </div>
    `;
    document.getElementById('ag-pe-login-form').addEventListener('submit', async (e) => {
      e.preventDefault();
      state.password = document.getElementById('ag-pe-pw').value;
      try {
        // smoke test the password by listing blogs
        const data = await api('list_blogs');
        state.blogs = data.blogs || [];
        sessionStorage.setItem('agpe_password', state.password);
        await goList();
      } catch (err) {
        state.password = '';
        sessionStorage.removeItem('agpe_password');
        renderLogin(err.message);
      }
    });
  }

  // ── List view ────────────────────────────────────────────
  async function goList() {
    state.view = 'list';
    state.bridgeSelection.clear();
    root.innerHTML = `
      <div class="ag-pe-tabs">
        <button class="ag-pe-tab is-active" data-tab="list">Entradas</button>
        <button class="ag-pe-tab" data-tab="new">+ Nueva entrada</button>
      </div>
      <div class="ag-pe-list__filter">
        <select class="ag-pe-select" id="ag-pe-list-blog">
          <option value="">Todos los blogs</option>
          ${state.blogs.map(b => `<option value="${escapeAttr(b.id)}">${escapeHtml(b.title)}</option>`).join('')}
        </select>
        <input type="search" class="ag-pe-input" id="ag-pe-list-search" placeholder="Buscar título…">
      </div>
      <div id="ag-pe-list-results">
        <div class="ag-pe-spinner-block"><span class="ag-pe-loading"></span>Cargando entradas…</div>
      </div>
    `;
    bindTabs();

    let debounce;
    document.getElementById('ag-pe-list-blog').addEventListener('change', loadList);
    document.getElementById('ag-pe-list-search').addEventListener('input', () => {
      clearTimeout(debounce);
      debounce = setTimeout(loadList, 250);
    });
    await loadList();
  }

  async function loadList() {
    const blogId = document.getElementById('ag-pe-list-blog').value;
    const query  = document.getElementById('ag-pe-list-search').value;
    const target = document.getElementById('ag-pe-list-results');
    target.innerHTML = `<div class="ag-pe-spinner-block"><span class="ag-pe-loading"></span>Cargando…</div>`;
    try {
      const data = await api('list_articles', { blog_id: blogId, query: query });
      state.articles = data.articles || [];
      renderListResults(target);
    } catch (err) {
      target.innerHTML = `<div class="ag-pe-error-banner">${escapeHtml(err.message)}</div>`;
    }
  }

  function renderListResults(target) {
    if (!state.articles.length) {
      target.innerHTML = `<div class="ag-pe-card"><p style="margin:0;color:var(--pe-muted)">Aún no hay entradas.</p></div>`;
      return;
    }
    const canBridge = !!bridgeLabel;
    const bar = canBridge ? `
      <div class="ag-pe-batch-bar">
        <label class="ag-pe-batch-all"><input type="checkbox" id="ag-pe-select-all"> Seleccionar todo</label>
        <span class="ag-pe-batch-count" id="ag-pe-batch-count">0 seleccionadas</span>
        <button type="button" class="ag-pe-btn ag-pe-btn--ghost ag-pe-btn--bridge ag-pe-btn--small" id="ag-pe-batch-push" disabled>${escapeHtml(bridgeLabel)}</button>
      </div>` : '';
    target.innerHTML = bar + `<div class="ag-pe-list">${state.articles.map(a => `
      <div class="ag-pe-list-item" data-article-id="${escapeAttr(a.id)}">
        ${canBridge ? `<input type="checkbox" class="ag-pe-list-item__check" data-article-id="${escapeAttr(a.id)}" aria-label="Seleccionar para ${escapeAttr(bridgeLabel)}">` : ''}
        <img class="ag-pe-list-item__thumb" src="${escapeAttr(a.imageUrl || '')}" alt="" onerror="this.style.visibility='hidden'">
        <div class="ag-pe-list-item__body">
          <h3 class="ag-pe-list-item__title">${escapeHtml(a.title)}</h3>
          <div class="ag-pe-list-item__meta">
            ${(() => {
              const s = articleStatus(a);
              return `<span class="ag-pe-list-item__status ag-pe-list-item__status--${s}">${articleStatusLabel(s)}</span>`;
            })()}
            <span>${escapeHtml(a.blogTitle || '')}</span>
            <span>${formatDate(a.publishedAt)}</span>
            ${a.authorName ? `<span>por ${escapeHtml(a.authorName)}</span>` : ''}
            ${a.bridged ? `<span class="ag-pe-list-item__bridged" title="Enviada a ${escapeAttr(a.bridgedStore || bridgeLabel || 'tienda asociada')}${a.bridgedAt ? ' · ' + escapeAttr(formatDate(a.bridgedAt)) : ''}">↔ En ${escapeHtml(bridgeTargetShort)}</span>` : ''}
          </div>
        </div>
      </div>
    `).join('')}</div>`;

    // Al hacer clic en la fila se abre el editor, salvo que el clic sea en la casilla.
    target.querySelectorAll('.ag-pe-list-item').forEach(el => {
      el.addEventListener('click', (e) => {
        if (e.target.closest('.ag-pe-list-item__check')) return;
        goEditor(el.getAttribute('data-article-id'));
      });
    });

    if (canBridge) wireBatchBridge(target);
  }

  // ── Envío por lotes a la tienda asociada ─────────────────
  function wireBatchBridge(target) {
    const countEl   = target.querySelector('#ag-pe-batch-count');
    const pushBtn   = target.querySelector('#ag-pe-batch-push');
    const selectAll = target.querySelector('#ag-pe-select-all');
    const checks    = Array.from(target.querySelectorAll('.ag-pe-list-item__check'));
    const sel       = state.bridgeSelection;

    function sync() {
      countEl.textContent = sel.size + ' seleccionadas';
      pushBtn.disabled = sel.size === 0;
      if (selectAll) selectAll.checked = checks.length > 0 && sel.size === checks.length;
    }
    checks.forEach(c => { c.checked = sel.has(c.getAttribute('data-article-id')); });
    sync();

    checks.forEach(c => {
      c.addEventListener('click', e => e.stopPropagation());
      c.addEventListener('change', () => {
        const id = c.getAttribute('data-article-id');
        if (c.checked) sel.add(id); else sel.delete(id);
        sync();
      });
    });
    if (selectAll) {
      selectAll.addEventListener('change', () => {
        checks.forEach(c => {
          c.checked = selectAll.checked;
          const id = c.getAttribute('data-article-id');
          if (selectAll.checked) sel.add(id); else sel.delete(id);
        });
        sync();
      });
    }
    pushBtn.addEventListener('click', () => pushSelectedToBridge(target));
  }

  async function pushSelectedToBridge(target) {
    const ids = Array.from(state.bridgeSelection);
    if (!ids.length) return;
    const ok = confirm(
      'Enviar ' + ids.length + ' entrada' + (ids.length > 1 ? 's' : '') + ' a ' + bridgeLabel + '?\n\n' +
      'Cada una se traduce automáticamente y se crea como BORRADOR en la otra tienda para revisión. ' +
      'No se publica nada. Los productos relacionados no se copian; los enlaces pueden necesitar reajuste.'
    );
    if (!ok) return;

    const pushBtn = target.querySelector('#ag-pe-batch-push');
    let done = 0, failed = 0;
    pushBtn.disabled = true;
    for (let i = 0; i < ids.length; i++) {
      pushBtn.innerHTML = `<span class="ag-pe-loading"></span>Enviando ${i + 1}/${ids.length}…`;
      try {
        const r = await api('bridge_push', { article_id: ids[i] }, 180000);
        done++;
        const art = state.articles.find(a => a.id === ids[i]);
        if (art) { art.bridged = true; if (r && r.targetStore) art.bridgedStore = r.targetStore; }
      } catch (err) {
        failed++;
      }
    }
    state.bridgeSelection.clear();
    toast(`Enviadas ${done} a ${bridgeTargetShort}` + (failed ? ` · ${failed} fallidas` : '') + '.', failed ? 'error' : 'success');
    renderListResults(target);
  }

  // ── Editor view ──────────────────────────────────────────
  async function goEditor(articleId) {
    state.view = 'editor';
    state.selectedProducts = [];
    state.featuredImage = { url: '', altText: '' };

    if (articleId) {
      root.innerHTML = `<div class="ag-pe-spinner-block"><span class="ag-pe-loading"></span>Cargando artículo…</div>`;
      try {
        const data = await api('get_article', { article_id: articleId });
        state.currentArticle = data.article;
      } catch (err) {
        root.innerHTML = `<div class="ag-pe-error-banner">${escapeHtml(err.message)}</div>`;
        return;
      }
    } else {
      state.currentArticle = null;
    }

    renderEditor();
    initQuill();

    if (state.currentArticle) {
      hydrateForm(state.currentArticle);
    }
  }

  function renderEditor() {
    const isEdit = !!state.currentArticle;
    root.innerHTML = `
      <div class="ag-pe-tabs">
        <button class="ag-pe-tab" data-tab="list">← Todas las entradas</button>
        <button class="ag-pe-tab is-active" data-tab="editor">${isEdit ? 'Editar entrada' : 'Nueva entrada'}</button>
      </div>

      <div class="ag-pe-grid">
        <div>
          <div class="ag-pe-card">
            <h3 class="ag-pe-card__title">Título y cuerpo</h3>
            <div class="ag-pe-field">
              <label class="ag-pe-label" for="ag-pe-title">Título</label>
              <input id="ag-pe-title" class="ag-pe-input" required maxlength="160">
            </div>
            <div class="ag-pe-field">
              <label class="ag-pe-label" for="ag-pe-summary">Extracto / resumen <small>1–2 frases que se muestran en la parte superior del artículo y en las tarjetas del listado</small></label>
              <textarea id="ag-pe-summary" class="ag-pe-textarea" rows="2" maxlength="500"></textarea>
            </div>
            <div class="ag-pe-field" id="ag-pe-body-field">
              <div class="ag-pe-body-head">
                <label class="ag-pe-label" style="margin:0">Cuerpo</label>
                <div class="ag-pe-body-head__actions">
                  <button type="button" class="ag-pe-btn ag-pe-btn--ghost ag-pe-btn--small ag-pe-fs-only" id="ag-pe-fs-save-draft">Guardar como borrador</button>
                  <button type="button" class="ag-pe-btn ag-pe-btn--small ag-pe-fs-only" id="ag-pe-fs-save">${isEdit ? 'Guardar cambios' : 'Crear entrada'}</button>
                  <button type="button" class="ag-pe-btn ag-pe-btn--ghost ag-pe-btn--small" id="ag-pe-fullscreen" title="Ampliar el editor a pantalla completa (Esc para cerrar)">⛶ Ampliar</button>
                </div>
              </div>
              <div class="ag-pe-quicklinks ag-pe-fs-only" id="ag-pe-quicklinks" data-ag-pe-quicklinks></div>
              <div class="ag-pe-editor" id="ag-pe-editor"></div>
            </div>
          </div>

          <div class="ag-pe-card">
            <h3 class="ag-pe-card__title">Puntos clave <small>3–5 viñetas breves, una por línea. Aparece en la parte superior del artículo + alimenta el fragmento de búsqueda con IA.</small></h3>
            <textarea id="ag-pe-takeaways" class="ag-pe-textarea" rows="4" placeholder="Las mancuernas ajustables ahorran espacio&#10;Un solo par va de 5 a 50 kg&#10;Cinco minutos de mantenimiento al mes las mantienen a punto"></textarea>
          </div>


          <div class="ag-pe-card">
            <h3 class="ag-pe-card__title">Ficha en buscadores (SEO)</h3>
            <div class="ag-pe-field">
              <label class="ag-pe-label" for="ag-pe-post-type">Tipo de entrada <small>Determina los datos estructurados que ven Google + los asistentes de IA</small></label>
              <select id="ag-pe-post-type" class="ag-pe-select">
                <option value="general">General</option>
                <option value="buyer-guide">Guía de compra</option>
                <option value="how-to">Tutorial (los H2 se convierten en pasos numerados en Google)</option>
                <option value="faq">FAQ (H2 = pregunta, párrafo siguiente = respuesta)</option>
              </select>
            </div>
            <div class="ag-pe-field">
              <label class="ag-pe-label" for="ag-pe-seo-title">Meta título <small>50–60 caracteres funciona mejor</small></label>
              <input id="ag-pe-seo-title" class="ag-pe-input" maxlength="70">
            </div>
            <div class="ag-pe-field">
              <label class="ag-pe-label" for="ag-pe-seo-desc">Meta descripción <small>150–160 caracteres funciona mejor</small></label>
              <textarea id="ag-pe-seo-desc" class="ag-pe-textarea" rows="2" maxlength="320"></textarea>
            </div>
            <div class="ag-pe-field">
              <label class="ag-pe-label" for="ag-pe-handle">Slug de URL <small>Déjalo en blanco para autogenerarlo a partir del título</small></label>
              <input id="ag-pe-handle" class="ag-pe-input" placeholder="slug-de-url-entrada">
            </div>
          </div>

          <div class="ag-pe-card">
            <h3 class="ag-pe-card__title">Biografía del autor <small>Refuerza las señales E-E-A-T en el contenido de entrenamiento / equipamiento</small></h3>
            <div class="ag-pe-field">
              <label class="ag-pe-label" for="ag-pe-author-bio">Biografía</label>
              <textarea id="ag-pe-author-bio" class="ag-pe-textarea" rows="3" placeholder="Biografía breve: cargo, credenciales, años de experiencia."></textarea>
            </div>
            <div class="ag-pe-field">
              <label class="ag-pe-label">Avatar del autor</label>
              <div class="ag-pe-image" id="ag-pe-avatar-wrap">
                <img class="ag-pe-image__preview" id="ag-pe-avatar-preview" style="display:none">
                <div class="ag-pe-avatar-actions">
                  <button type="button" class="ag-pe-btn ag-pe-btn--ghost ag-pe-btn--small" id="ag-pe-avatar-pick">Subir avatar</button>
                  <button type="button" class="ag-pe-btn ag-pe-btn--ghost ag-pe-btn--small" id="ag-pe-avatar-choose">Elegir anterior…</button>
                </div>
                <input type="file" id="ag-pe-avatar-input" accept="image/*">
              </div>
            </div>
            <div class="ag-pe-field">
              <label class="ag-pe-label" for="ag-pe-author-linkedin">LinkedIn URL</label>
              <input id="ag-pe-author-linkedin" class="ag-pe-input" placeholder="https://www.linkedin.com/in/…">
            </div>
          </div>
        </div>

        <aside>
          <div class="ag-pe-card">
            <h3 class="ag-pe-card__title">Estado</h3>
            <div class="ag-pe-field">
              <label class="ag-pe-label" for="ag-pe-status">Visibilidad</label>
              <select id="ag-pe-status" class="ag-pe-select">
                <option value="draft">Borrador (oculto)</option>
                <option value="publish">Publicar ahora (visible)</option>
                <option value="schedule">Programar para más tarde</option>
              </select>
            </div>
            <div class="ag-pe-field" id="ag-pe-schedule-wrap" style="display:none">
              <label class="ag-pe-label" for="ag-pe-schedule">Publicar el <small>tu hora local</small></label>
              <input type="datetime-local" id="ag-pe-schedule" class="ag-pe-input">
              <p class="ag-pe-help">El artículo permanece oculto hasta esta fecha/hora, y luego Shopify lo publica automáticamente.</p>
            </div>
          </div>

          <div class="ag-pe-card">
            <h3 class="ag-pe-card__title">Imagen destacada</h3>
            <div class="ag-pe-image" id="ag-pe-image-wrap">
              <img class="ag-pe-image__preview" id="ag-pe-image-preview" style="display:none">
              <p class="ag-pe-image__hint" id="ag-pe-image-hint">JPG/PNG/WebP, idealmente 1600×1200+.</p>
              <button type="button" class="ag-pe-btn ag-pe-btn--ghost ag-pe-btn--small" id="ag-pe-image-pick">Subir imagen</button>
              <button type="button" class="ag-pe-btn ag-pe-btn--ghost ag-pe-btn--small" id="ag-pe-image-clear" style="display:none">Quitar</button>
              <input type="file" id="ag-pe-image-input" accept="image/*">
            </div>
            <div class="ag-pe-field" style="margin-top:10px">
              <label class="ag-pe-label" for="ag-pe-image-alt">Texto alternativo de la imagen <small>describe la imagen para SEO + accesibilidad</small></label>
              <input id="ag-pe-image-alt" class="ag-pe-input">
            </div>
          </div>

          <div class="ag-pe-card">
            <h3 class="ag-pe-card__title">Organización</h3>
            <div class="ag-pe-field">
              <label class="ag-pe-label" for="ag-pe-blog">Blog</label>
              <select id="ag-pe-blog" class="ag-pe-select" required ${isEdit ? 'disabled' : ''}>
                <option value="">Elige un blog…</option>
                ${state.blogs.map(b => `<option value="${escapeAttr(b.id)}">${escapeHtml(b.title)}</option>`).join('')}
              </select>
              ${isEdit ? `<p class="ag-pe-help">Las entradas existentes no pueden cambiar de blog, elimínala y vuelve a crearla para moverla.</p>` : ''}
            </div>
            <div class="ag-pe-field">
              <label class="ag-pe-label" for="ag-pe-author">Autor</label>
              <input id="ag-pe-author" class="ag-pe-input">
            </div>
            <div class="ag-pe-field">
              <label class="ag-pe-label">Etiquetas <small>escribe y pulsa intro o coma para añadir</small></label>
              <div class="ag-pe-chips" id="ag-pe-tags">
                <input type="text" class="ag-pe-chips__input" id="ag-pe-tags-input" placeholder="Añadir una etiqueta…">
              </div>
            </div>
          </div>

          <div class="ag-pe-card">
            <h3 class="ag-pe-card__title">Productos relacionados <small>se muestran en el cuerpo del artículo</small></h3>
            <div class="ag-pe-prod-picker">
              <input type="search" class="ag-pe-input" id="ag-pe-prod-search" placeholder="Buscar productos…">
              <div class="ag-pe-prod-results" id="ag-pe-prod-results" style="display:none"></div>
            </div>
            <div class="ag-pe-prod-selected" id="ag-pe-prod-selected"></div>
          </div>

          <div class="ag-pe-card ag-pe-checklist-card">
            <h3 class="ag-pe-card__title">Lista de comprobación SEO</h3>
            <ul class="ag-pe-checklist" id="ag-pe-checklist"></ul>
          </div>
        </aside>
      </div>

      <div class="ag-pe-btn-row">
        <button class="ag-pe-btn" id="ag-pe-save">${isEdit ? 'Guardar cambios' : 'Crear entrada'}</button>
        <button class="ag-pe-btn ag-pe-btn--ghost" id="ag-pe-save-draft" title="Guardar sin publicar, cambia la entrada a Borrador independientemente del ajuste de Visibilidad de arriba">Guardar como borrador</button>
        <button class="ag-pe-btn ag-pe-btn--ghost" id="ag-pe-cancel">Cancelar</button>
        ${isEdit && state.currentArticle ? (() => {
          const a = state.currentArticle;
          const idNum = String(a.id || '').split('/').pop();
          const adminUrl = `https://xh60ip-pk.myshopify.com/admin/articles/${escapeAttr(idNum)}`;
          // Public URL only shown for live posts with valid handles,
          // and even then, can 404 if Shopify auto-modified the handle
          // on save or the publishedAt is still in the future. The
          // admin button is the reliable fallback (it always works
          // and has Shopify's own Preview button inside).
          let html = '';
          if (a.isPublished && a.blogHandle && a.handle) {
            const url = `/blogs/${escapeAttr(a.blogHandle)}/${escapeAttr(a.handle)}`;
            html += `<a class="ag-pe-btn ag-pe-btn--ghost" href="${url}" target="_blank" title="${escapeAttr(url)}">Ver en vivo ↗</a>`;
          }
          html += `<a class="ag-pe-btn ag-pe-btn--ghost" href="${adminUrl}" target="_blank" title="Siempre funciona: la página del artículo en el admin de Shopify tiene su propio botón de Vista previa para borradores + entradas programadas">Abrir en el admin ↗</a>`;
          return html;
        })() : ''}
        ${isEdit && state.currentArticle ? `
          <div class="ag-pe-move-wrap">
            <select id="ag-pe-move-target" class="ag-pe-select">
              <option value="">Mover a blog…</option>
              ${state.blogs.filter(b => b.id !== state.currentArticle.blogId).map(b => `<option value="${escapeAttr(b.id)}">${escapeHtml(b.title)}</option>`).join('')}
            </select>
            <button type="button" class="ag-pe-btn ag-pe-btn--ghost ag-pe-btn--danger" id="ag-pe-move">Mover</button>
          </div>
        ` : ''}
        ${isEdit && state.currentArticle && bridgeLabel ? `
          <button type="button" class="ag-pe-btn ag-pe-btn--ghost ag-pe-btn--bridge" id="ag-pe-bridge" title="Traduce automáticamente esta entrada y la crea como BORRADOR en la otra tienda para revisión. Los productos relacionados no se copian y los enlaces pueden necesitar reajuste.">${escapeHtml(bridgeLabel)}</button>
        ` : ''}
      </div>
    `;
    bindTabs();
    bindEditorEvents();
  }

  function initQuill() {
    if (!window.Quill) {
      // Quill not yet loaded. Retry shortly
      setTimeout(initQuill, 150);
      return;
    }
    const target = document.getElementById('ag-pe-editor');
    if (!target) return;

    // Register quill-table-up if its UMD bundle loaded. The package exposes
    // its main class as window.TableUp.default and helpers as named exports
    // on the same object (defaultCustomSelect, TableMenuContextmenu, etc.).
    let TU = null;
    if (window.TableUp) {
      TU = window.TableUp.default || window.TableUp;
      try {
        Quill.register({ ['modules/' + TU.moduleName]: TU }, true);
      } catch (e) {
        TU = null;
      }
    }

    const toolbarContainer = [
      [{ header: [2, 3, 4, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['blockquote', 'code-block'],
      ['link', 'image', 'ag-product-link', 'ag-collection-link'],
      [{ align: [] }],
      [{ color: [] }]
    ];
    if (TU) toolbarContainer.push([{ [TU.toolName]: [] }]);
    toolbarContainer.push(['clean']);

    const modules = {
      toolbar: {
        container: toolbarContainer,
        handlers: {
          image: handleEditorImage,
          'ag-product-link': () => openLinkPicker('product'),
          'ag-collection-link': () => openLinkPicker('collection')
        }
      }
    };
    if (TU) {
      const tu = window.TableUp;
      modules[TU.moduleName] = {
        full: true,
        customSelect: tu.defaultCustomSelect,
        customBtn: true,
        texts: { customBtnText: 'Tamaño personalizado', confirmText: 'Insertar', cancelText: 'Cancelar' },
        modules: [
          tu.TableResizeBox && { module: tu.TableResizeBox },
          tu.TableResizeLine && { module: tu.TableResizeLine },
          tu.TableSelection && { module: tu.TableSelection },
          tu.TableMenuContextmenu && { module: tu.TableMenuContextmenu },
          tu.TableAlign && { module: tu.TableAlign }
        ].filter(Boolean)
      };
    }

    state.quill = new Quill(target, { theme: 'snow', modules: modules, placeholder: 'Escribe tu entrada…' });

    if (state.currentArticle && state.currentArticle.body) {
      state.quill.clipboard.dangerouslyPasteHTML(state.currentArticle.body);
    }

    // Track the last known cursor position. Clicking a sidebar/header
    // button (e.g. a Quick-link chip) steals focus from Quill, which
    // makes Quill.getSelection() return null at click-time. And the
    // insert lands at the end of the document. Listening to
    // selection-change lets us remember where the cursor WAS just
    // before the user clicked the chip, and insert there instead.
    state.lastRange = { index: state.quill.getLength(), length: 0 };
    state.quill.on('selection-change', function (range) {
      if (range) state.lastRange = range;
    });
  }

  // ── Fullscreen body editor ────────────────────────────────────
  // Toggle the body field into a viewport-filling overlay. The
  // overlay has its own scroll context, so the Quill toolbar's
  // CSS position:sticky actually works (the theme's ancestor
  // overflow that was breaking sticky doesn't apply inside the
  // fixed element). Escape key + the close button both exit.
  function toggleEditorFullscreen() {
    const field = document.getElementById('ag-pe-body-field');
    if (!field) return;
    const becoming = !field.classList.contains('is-fullscreen');
    field.classList.toggle('is-fullscreen', becoming);
    document.documentElement.classList.toggle('ag-pe-scroll-lock', becoming);
    const btn = document.getElementById('ag-pe-fullscreen');
    if (btn) btn.innerHTML = becoming ? '✕ Cerrar' : '⛶ Ampliar';
    if (becoming && state.quill) state.quill.focus();
  }

  function handleEditorImage() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.click();
    input.onchange = async () => {
      const file = input.files && input.files[0];
      if (!file) return;
      const altText = (prompt('Texto alternativo de esta imagen (describe brevemente lo que muestra, obligatorio para SEO + accesibilidad):', '') || '').trim();
      try {
        toast('Subiendo imagen…');
        const prepared = await prepareImage(file);
        const base64 = await readFileAsBase64(prepared);
        const result = await api('upload_image', {
          filename: file.name,
          mimeType: prepared.type || file.type,
          base64: base64,
          altText: altText
        });
        const range = state.quill.getSelection(true);
        state.quill.insertEmbed(range.index, 'image', result.url, 'user');
        // Add alt attribute to the freshly inserted image
        if (altText) {
          requestAnimationFrame(() => {
            const editorRoot = state.quill.root;
            const imgs = editorRoot.querySelectorAll('img[src="' + result.url + '"]:not([alt])');
            imgs.forEach(img => img.setAttribute('alt', altText));
          });
        }
        state.quill.setSelection(range.index + 1);
        updateChecklist();
      } catch (err) {
        toast('Error al subir la imagen: ' + friendlyError(err), 'error');
      }
    };
  }

  function bindEditorEvents() {
    // Tag chip input
    const tagInput = document.getElementById('ag-pe-tags-input');
    tagInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ',') {
        e.preventDefault();
        addTag(tagInput.value.trim());
        tagInput.value = '';
      } else if (e.key === 'Backspace' && tagInput.value === '') {
        const chips = document.querySelectorAll('#ag-pe-tags .ag-pe-chip');
        if (chips.length) chips[chips.length - 1].remove();
      }
    });
    tagInput.addEventListener('blur', () => {
      if (tagInput.value.trim()) {
        addTag(tagInput.value.trim());
        tagInput.value = '';
      }
    });

    // Author avatar upload
    document.getElementById('ag-pe-avatar-pick').addEventListener('click', () => {
      document.getElementById('ag-pe-avatar-input').click();
    });
    // Guarded: a stale-cache scenario (new JS, old HTML) would null this
    // and break the rest of the binding chain (including Save). Skip if
    // the button isn't in the DOM rather than throwing.
    const chooseAvatarBtn = document.getElementById('ag-pe-avatar-choose');
    if (chooseAvatarBtn) chooseAvatarBtn.addEventListener('click', openAuthorPicker);
    document.getElementById('ag-pe-avatar-input').addEventListener('change', async (e) => {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      const btn = document.getElementById('ag-pe-avatar-pick');
      btn.disabled = true;
      btn.innerHTML = '<span class="ag-pe-loading"></span>Subiendo…';
      try {
        const prepared = await prepareImage(file);
        const base64 = await readFileAsBase64(prepared);
        const result = await api('upload_image', {
          filename: file.name, mimeType: prepared.type || file.type, base64: base64, altText: ''
        });
        state.authorAvatarUrl = result.url;
        const preview = document.getElementById('ag-pe-avatar-preview');
        preview.src = result.url; preview.style.display = 'block';
        btn.textContent = 'Reemplazar avatar';
      } catch (err) {
        toast('Error al subir el avatar: ' + friendlyError(err), 'error');
      } finally {
        btn.disabled = false;
      }
    });

    // Image upload
    document.getElementById('ag-pe-image-pick').addEventListener('click', () => {
      document.getElementById('ag-pe-image-input').click();
    });
    document.getElementById('ag-pe-image-clear').addEventListener('click', () => {
      state.featuredImage = { url: '', altText: '' };
      document.getElementById('ag-pe-image-preview').style.display = 'none';
      document.getElementById('ag-pe-image-clear').style.display = 'none';
      document.getElementById('ag-pe-image-hint').style.display = 'block';
      document.getElementById('ag-pe-image-alt').value = '';
    });
    document.getElementById('ag-pe-image-input').addEventListener('change', async (e) => {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      const btn = document.getElementById('ag-pe-image-pick');
      btn.disabled = true;
      btn.innerHTML = '<span class="ag-pe-loading"></span>Subiendo…';
      try {
        const prepared = await prepareImage(file);
        const base64 = await readFileAsBase64(prepared);
        const result = await api('upload_image', {
          filename: file.name,
          mimeType: prepared.type || file.type,
          base64: base64,
          altText: document.getElementById('ag-pe-image-alt').value || ''
        });
        state.featuredImage.url = result.url;
        showFeaturedImage(result.url);
      } catch (err) {
        toast('Error al subir la imagen: ' + friendlyError(err), 'error');
      } finally {
        btn.disabled = false;
        btn.textContent = 'Reemplazar imagen';
      }
    });

    // Product search. Uses Shopify's storefront predictive search endpoint
    // (no Apps Script round-trip → ~100ms vs ~2s)
    const prodSearch = document.getElementById('ag-pe-prod-search');
    const prodResults = document.getElementById('ag-pe-prod-results');
    let prodDebounce;
    prodSearch.addEventListener('focus', async () => {
      if (!prodSearch.value.trim()) {
        // Show some defaults so the picker isn't empty
        try {
          const defaults = await fetchStorefrontProducts('', 8);
          if (defaults.length) renderProductResults(defaults);
        } catch (e) { /* non-fatal */ }
      }
    });
    prodSearch.addEventListener('input', () => {
      clearTimeout(prodDebounce);
      const q = prodSearch.value.trim();
      prodDebounce = setTimeout(async () => {
        try {
          const products = await fetchStorefrontProducts(q, 10);
          renderProductResults(products);
        } catch (err) {
          prodResults.innerHTML = `<div class="ag-pe-prod-result">${escapeHtml(err.message)}</div>`;
          prodResults.style.display = 'block';
        }
      }, 150);
    });
    document.addEventListener('click', (e) => {
      if (!prodResults.contains(e.target) && e.target !== prodSearch) {
        prodResults.style.display = 'none';
      }
    });

    // Save / cancel
    document.getElementById('ag-pe-save').addEventListener('click', () => save());
    document.getElementById('ag-pe-save-draft').addEventListener('click', () => save({ forceDraft: true }));
    document.getElementById('ag-pe-status').addEventListener('change', updateScheduleVisibility);
    document.getElementById('ag-pe-cancel').addEventListener('click', () => {
      if (confirm('¿Descartar los cambios?')) goList();
    });
    // Fullscreen-only mirrored buttons (visible only when expanded)
    const fsSave = document.getElementById('ag-pe-fs-save');
    if (fsSave) fsSave.addEventListener('click', () => save());
    const fsSaveDraft = document.getElementById('ag-pe-fs-save-draft');
    if (fsSaveDraft) fsSaveDraft.addEventListener('click', () => save({ forceDraft: true }));

    // Populate the quick-links sidebar card + warm the authors cache
    // in the background so the picker opens instantly on first click.
    renderQuickLinks();
    loadQuickLinksFromBackend(); // override with backend-stored value if set
    // Fullscreen body editor toggle
    const fsBtn = document.getElementById('ag-pe-fullscreen');
    if (fsBtn) fsBtn.addEventListener('click', toggleEditorFullscreen);
    // Esc closes fullscreen
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        const f = document.getElementById('ag-pe-body-field');
        if (f && f.classList.contains('is-fullscreen')) toggleEditorFullscreen();
      }
    });
    if (!_authorsCache) {
      api('list_authors', {}).then(r => { _authorsCache = r.authors || []; }).catch(() => {});
    }

    // Move post (edit mode only)
    const moveBtn = document.getElementById('ag-pe-move');
    if (moveBtn) {
      moveBtn.addEventListener('click', moveToBlog);
    }

    // Enviar a la tienda asociada (solo en modo edición)
    const bridgeBtn = document.getElementById('ag-pe-bridge');
    if (bridgeBtn) {
      bridgeBtn.addEventListener('click', pushToBridge);
    }

    // Auto-slug from title
    document.getElementById('ag-pe-title').addEventListener('blur', () => {
      const handleField = document.getElementById('ag-pe-handle');
      if (!handleField.value) {
        handleField.placeholder = slugify(document.getElementById('ag-pe-title').value);
      }
    });

    // Live SEO checklist. Recompute on any input change
    const watchSel = '#ag-pe-title, #ag-pe-summary, #ag-pe-seo-title, #ag-pe-seo-desc, #ag-pe-image-alt, #ag-pe-takeaways, #ag-pe-author-bio';
    document.querySelectorAll(watchSel).forEach(el => {
      el.addEventListener('input', updateChecklist);
    });
    if (state.quill) state.quill.on('text-change', updateChecklist);
    updateChecklist();
  }

  // ── SEO checklist ──────────────────────────────────────
  function updateChecklist() {
    const out = document.getElementById('ag-pe-checklist');
    if (!out) return;
    const title = document.getElementById('ag-pe-title').value || '';
    const summary = document.getElementById('ag-pe-summary').value || '';
    const seoTitle = document.getElementById('ag-pe-seo-title').value || '';
    const seoDesc  = document.getElementById('ag-pe-seo-desc').value || '';
    const imageAlt = document.getElementById('ag-pe-image-alt').value || '';
    const takeaways= document.getElementById('ag-pe-takeaways').value || '';
    const authorBio= document.getElementById('ag-pe-author-bio').value || '';

    const bodyHtml = state.quill ? state.quill.root.innerHTML : '';
    const bodyText = (state.quill ? state.quill.getText() : '').trim();
    const words = bodyText ? bodyText.split(/\s+/).length : 0;

    // Inline images in the body
    const imgs = (bodyHtml.match(/<img\b[^>]*>/gi) || []);
    const imgsMissingAlt = imgs.filter(t => !/\balt\s*=\s*"[^"]/i.test(t)).length;

    // Internal links, relative paths or stealth-equipment.com
    const internalLinkPattern = /<a\b[^>]*href\s*=\s*"(?:\/|https?:\/\/[^"]*stealth-equipment\.com)/gi;
    const internalLinks = (bodyHtml.match(internalLinkPattern) || []).length;

    // Subheadings (H2/H3)
    const h2h3Count = (bodyHtml.match(/<h[23]\b/gi) || []).length;

    const items = [
      { ok: title.length > 0 && title.length <= 70, label: 'Título definido (≤ 70 caracteres)', detail: title.length + ' caracteres' },
      { ok: summary.length >= 80, label: 'Extracto ≥ 80 caracteres (entradilla + fragmento de Google)', detail: summary.length + ' caracteres' },
      { ok: !!state.featuredImage.url, label: 'Imagen destacada subida' },
      { ok: imageAlt.trim().length > 0, label: 'Texto alternativo de la imagen destacada definido' },
      { ok: words >= 300, label: 'Cuerpo ≥ 300 palabras', detail: words + ' palabras' },
      { ok: h2h3Count >= 2, label: 'Al menos 2 subtítulos (H2/H3)', detail: h2h3Count },
      { ok: imgs.length === 0 || imgsMissingAlt === 0, label: 'Todas las imágenes del cuerpo tienen texto alternativo', detail: imgs.length ? (imgsMissingAlt + ' sin texto alternativo') : 'sin imágenes en el cuerpo' },
      { ok: internalLinks >= 1, label: 'Al menos 1 enlace interno', detail: internalLinks + ' enlace(s)' },
      { ok: takeaways.trim().length > 0, label: 'Puntos clave rellenados' },
      { ok: seoTitle.length >= 40 && seoTitle.length <= 65, label: 'Meta título 40–65 caracteres', detail: seoTitle.length + (seoTitle ? ' caracteres' : ' (se usa el título de la entrada)') },
      { ok: seoDesc.length >= 120 && seoDesc.length <= 165, label: 'Meta descripción 120–165 caracteres', detail: seoDesc.length + ' caracteres' },
      { ok: getTags().length >= 3, label: '≥ 3 etiquetas', detail: getTags().length + ' etiqueta(s)' },
      { ok: authorBio.trim().length > 0, label: 'Biografía del autor definida (E-E-A-T)' }
    ];

    out.innerHTML = items.map(i => `
      <li class="ag-pe-checklist__item ${i.ok ? 'is-ok' : 'is-todo'}">
        <span class="ag-pe-checklist__mark">${i.ok ? '✓' : '○'}</span>
        <span class="ag-pe-checklist__label">${escapeHtml(i.label)}${i.detail != null ? ` <small>${escapeHtml(String(i.detail))}</small>` : ''}</span>
      </li>
    `).join('');
  }

  function renderProductResults(products) {
    const wrap = document.getElementById('ag-pe-prod-results');
    if (!products.length) {
      wrap.innerHTML = `<div class="ag-pe-prod-result">Sin resultados.</div>`;
      wrap.style.display = 'block';
      return;
    }
    wrap.innerHTML = products.map(p => `
      <div class="ag-pe-prod-result" data-product-id="${escapeAttr(p.id)}">
        <img src="${escapeAttr(p.imageUrl || '')}" alt="" onerror="this.style.visibility='hidden'">
        <span>${escapeHtml(p.title)}</span>
      </div>
    `).join('');
    wrap.style.display = 'block';
    wrap.querySelectorAll('.ag-pe-prod-result[data-product-id]').forEach(el => {
      el.addEventListener('click', () => {
        const id = el.getAttribute('data-product-id');
        const p = products.find(x => x.id === id);
        if (p) addProduct(p);
        wrap.style.display = 'none';
        document.getElementById('ag-pe-prod-search').value = '';
      });
    });
  }

  function addProduct(p) {
    if (state.selectedProducts.find(x => x.id === p.id)) return;
    state.selectedProducts.push(p);
    renderSelectedProducts();
  }

  function removeProduct(id) {
    state.selectedProducts = state.selectedProducts.filter(p => p.id !== id);
    renderSelectedProducts();
  }

  function renderSelectedProducts() {
    const wrap = document.getElementById('ag-pe-prod-selected');
    wrap.innerHTML = state.selectedProducts.map(p => `
      <div class="ag-pe-prod-row">
        <img src="${escapeAttr(p.imageUrl || '')}" alt="" onerror="this.style.visibility='hidden'">
        <span class="ag-pe-prod-row__title">${escapeHtml(p.title)}</span>
        <button type="button" class="ag-pe-btn ag-pe-btn--ghost ag-pe-btn--small" data-remove="${escapeAttr(p.id)}">Quitar</button>
      </div>
    `).join('');
    wrap.querySelectorAll('[data-remove]').forEach(b => {
      b.addEventListener('click', () => removeProduct(b.getAttribute('data-remove')));
    });
  }

  // Tags
  function addTag(text) {
    if (!text) return;
    const wrap = document.getElementById('ag-pe-tags');
    const exists = Array.from(wrap.querySelectorAll('.ag-pe-chip')).some(
      c => c.getAttribute('data-tag').toLowerCase() === text.toLowerCase()
    );
    if (exists) return;
    const chip = document.createElement('span');
    chip.className = 'ag-pe-chip';
    chip.setAttribute('data-tag', text);
    chip.innerHTML = `${escapeHtml(text)}<button type="button" class="ag-pe-chip__remove" aria-label="Quitar">&times;</button>`;
    chip.querySelector('.ag-pe-chip__remove').addEventListener('click', () => chip.remove());
    wrap.insertBefore(chip, document.getElementById('ag-pe-tags-input'));
  }

  function getTags() {
    return Array.from(document.querySelectorAll('#ag-pe-tags .ag-pe-chip'))
      .map(c => c.getAttribute('data-tag'));
  }

  // ── Author picker modal ────────────────────────────────────────
  // Fetches the unique author roster from the backend (cached 5min
  // server-side) and renders a grid of avatars. Click an author →
  // populates the current post's author name, avatar URL, bio,
  // linkedin fields. Saves re-uploading the same avatar per post.
  let _authorsCache = null;
  let _authorPickerOpen = false;
  async function openAuthorPicker() {
    if (_authorPickerOpen) return; // ignore double-clicks while one is open
    _authorPickerOpen = true;

    // Loading overlay shown immediately so the user knows the click
    // landed. Replaced with the real picker once the fetch resolves.
    const loadingOverlay = document.createElement('div');
    loadingOverlay.className = 'ag-pe-author-overlay';
    loadingOverlay.innerHTML = `
      <div class="ag-pe-author-modal" role="dialog">
        <header class="ag-pe-author-modal__head">
          <h3>Cargando autores…</h3>
          <button type="button" class="ag-pe-author-modal__close" aria-label="Cerrar">×</button>
        </header>
        <p class="ag-pe-author-modal__lede" style="display:flex;align-items:center;gap:8px"><span class="ag-pe-loading"></span> Obteniendo la lista de autores de Shopify…</p>
      </div>
    `;
    function bail() { loadingOverlay.remove(); _authorPickerOpen = false; }
    document.body.appendChild(loadingOverlay);
    loadingOverlay.querySelector('.ag-pe-author-modal__close').addEventListener('click', bail);
    loadingOverlay.addEventListener('click', (e) => { if (e.target === loadingOverlay) bail(); });

    let authors;
    try {
      if (_authorsCache) {
        authors = _authorsCache;
      } else {
        const r = await api('list_authors', {});
        authors = r.authors || [];
        _authorsCache = authors;
      }
    } catch (err) {
      bail();
      return toast('No se pudieron cargar los autores: ' + friendlyError(err), 'error');
    }

    // Replace the loading overlay with the real picker
    loadingOverlay.remove();

    if (!authors.length) {
      _authorPickerOpen = false;
      return toast('No se han encontrado autores anteriores, sube un avatar para crear el primero.', 'info');
    }

    const overlay = document.createElement('div');
    overlay.className = 'ag-pe-author-overlay';
    overlay.innerHTML = `
      <div class="ag-pe-author-modal" role="dialog" aria-labelledby="ag-pe-author-modal-title">
        <header class="ag-pe-author-modal__head">
          <h3 id="ag-pe-author-modal-title">Elige un autor</h3>
          <button type="button" class="ag-pe-author-modal__close" aria-label="Cerrar">×</button>
        </header>
        <p class="ag-pe-author-modal__lede">Haz clic en un autor para rellenar los campos de nombre + avatar + biografía + LinkedIn. Podrás editar cualquiera de ellos después.</p>
        <div class="ag-pe-author-grid">
          ${authors.map((a, i) => `
            <button type="button" class="ag-pe-author-card" data-idx="${i}">
              ${a.avatar
                ? `<img class="ag-pe-author-card__img" src="${escapeAttr(a.avatar)}" alt="${escapeAttr(a.name)}" onerror="this.style.visibility='hidden'">`
                : `<span class="ag-pe-author-card__noimg">${escapeHtml(a.name.charAt(0).toUpperCase())}</span>`}
              <span class="ag-pe-author-card__name">${escapeHtml(a.name)}</span>
              <span class="ag-pe-author-card__usage">${a.usageCount} entrada${a.usageCount === 1 ? '' : 's'}</span>
            </button>
          `).join('')}
        </div>
      </div>
    `;
    document.body.appendChild(overlay);

    function close() { overlay.remove(); _authorPickerOpen = false; }
    overlay.querySelector('.ag-pe-author-modal__close').addEventListener('click', close);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

    overlay.querySelectorAll('.ag-pe-author-card').forEach(btn => {
      btn.addEventListener('click', () => {
        const a = authors[parseInt(btn.dataset.idx, 10)];
        document.getElementById('ag-pe-author').value = a.name || '';
        document.getElementById('ag-pe-author-bio').value = a.bio || '';
        document.getElementById('ag-pe-author-linkedin').value = a.linkedin || '';
        if (a.avatar) {
          state.authorAvatarUrl = a.avatar;
          const preview = document.getElementById('ag-pe-avatar-preview');
          preview.src = a.avatar; preview.style.display = 'block';
          document.getElementById('ag-pe-avatar-pick').textContent = 'Reemplazar avatar';
        }
        close();
        toast('Autor cargado: revisa y guarda', 'success');
      });
    });
  }

  // ── Link picker modal (products + collections) ─────────────────
  // Opens a searchable modal for inserting product or collection
  // links into the Quill body at the current cursor. If the user has
  // text selected, that text becomes the link text. Otherwise the
  // item's title is inserted as both text and href.
  async function openLinkPicker(kind) {
    const q = state.quill;
    if (!q) return;
    // Snapshot the selection NOW. Quill loses focus when the modal
    // opens, so we can't read it later.
    const savedRange = q.getSelection() || { index: q.getLength(), length: 0 };
    const action = kind === 'collection' ? 'list_collections' : 'list_products';
    const title = kind === 'collection' ? 'Insertar enlace a colección' : 'Insertar enlace a producto';
    const placeholder = kind === 'collection' ? 'Buscar colecciones…' : 'Buscar productos…';

    const overlay = document.createElement('div');
    overlay.className = 'ag-pe-link-overlay';
    overlay.innerHTML = `
      <div class="ag-pe-link-modal" role="dialog">
        <header class="ag-pe-link-modal__head">
          <h3>${title}</h3>
          <button type="button" class="ag-pe-link-modal__close" aria-label="Cerrar">×</button>
        </header>
        <input type="search" class="ag-pe-link-modal__search" placeholder="${placeholder}" autofocus>
        <div class="ag-pe-link-modal__results" data-ag-link-results>
          <p class="ag-pe-link-modal__hint">${kind === 'collection' ? 'Cargando colecciones…' : 'Escribe para buscar…'}</p>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);
    const input = overlay.querySelector('.ag-pe-link-modal__search');
    const results = overlay.querySelector('[data-ag-link-results]');

    function close() { overlay.remove(); }
    overlay.querySelector('.ag-pe-link-modal__close').addEventListener('click', close);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

    function insert(url, title) {
      const range = savedRange;
      if (range.length > 0) {
        q.format('link', url);
      } else {
        q.insertText(range.index, title, { link: url });
        q.setSelection(range.index + title.length, 0);
      }
      close();
    }

    function render(items) {
      if (!items.length) {
        results.innerHTML = `<p class="ag-pe-link-modal__hint">Sin resultados. Prueba con otra palabra.</p>`;
        return;
      }
      results.innerHTML = items.map((it, i) => `
        <button type="button" class="ag-pe-link-row" data-idx="${i}">
          ${it.imageUrl ? `<img class="ag-pe-link-row__img" src="${escapeAttr(it.imageUrl)}" alt="" onerror="this.style.visibility='hidden'">` : '<span class="ag-pe-link-row__img ag-pe-link-row__img--placeholder"></span>'}
          <span class="ag-pe-link-row__body">
            <span class="ag-pe-link-row__title">${escapeHtml(it.title)}</span>
            <span class="ag-pe-link-row__url">${escapeHtml((kind === 'collection' ? it.url : '/products/' + it.handle))}</span>
          </span>
        </button>
      `).join('');
      results.querySelectorAll('.ag-pe-link-row').forEach(btn => {
        btn.addEventListener('click', () => {
          const it = items[parseInt(btn.dataset.idx, 10)];
          const url = kind === 'collection' ? it.url : '/products/' + it.handle;
          insert(url, it.title);
        });
      });
    }

    async function fetchAndRender(query) {
      try {
        const payload = kind === 'collection' ? { query: query || '' } : { query: query || '' };
        const r = await api(action, payload);
        const items = (kind === 'collection' ? r.collections : r.products) || [];
        render(items);
      } catch (err) {
        results.innerHTML = `<p class="ag-pe-link-modal__hint">No se pudo cargar: ${escapeHtml(friendlyError(err))}</p>`;
      }
    }

    // Collections load immediately (no query needed); products only on type
    if (kind === 'collection') fetchAndRender('');

    let t;
    input.addEventListener('input', () => {
      clearTimeout(t);
      const v = input.value.trim();
      if (kind === 'product' && !v) {
        results.innerHTML = `<p class="ag-pe-link-modal__hint">Escribe para buscar…</p>`;
        return;
      }
      t = setTimeout(() => fetchAndRender(v), 220);
    });
  }

  // Quick Links bar in the fullscreen body editor. Editable via the
  // section's "Quick Links" textarea setting in the customizer:
  // one per line, format `Label | URL`. Parsed below; falls back to
  // a built-in default set if the setting is blank.
  const QUICK_LINKS_DEFAULT = [
    // EU handles only: /collections/adjustable-dumbbells-1, /collections/kettlebells,
    // /collections/all-things-stealth and /pages/faq are UK handles and 404 here
    // (checked 2026-09-28).
    { label: 'Mancuernas ajustables', url: '/collections/mancuernas-ajustables' },
    { label: 'Todo el material',      url: '/collections/all' },
    { label: 'Garantía',              url: '/pages/warranty-information' },
    { label: 'Devoluciones',          url: '/pages/returns-or-faults' },
    { label: 'Envíos',                url: '/pages/shipping-information' },
    { label: 'Contacto',              url: '/pages/contact' },
    { label: 'Preguntas frecuentes',  url: '/pages/preguntas-frecuente' }
  ];
  // Normalises a raw URL string:
  //   - Already has http/https → leave as-is
  //   - Relative path starting with `/` → leave as-is
  //   - Bare domain or domain+path (e.g. assassingoods.com, foo.com/bar)
  //     → prepend https://
  //   - Anchor or query-string only → leave as-is
  function normalizeUrl(url) {
    const u = String(url || '').trim();
    if (!u) return '';
    if (/^https?:\/\//i.test(u)) return u;
    if (u.charAt(0) === '/' || u.charAt(0) === '#' || u.charAt(0) === '?') return u;
    if (/^mailto:|^tel:/i.test(u)) return u;
    // Anything else with a dot before the first slash is likely a domain
    if (u.indexOf('.') !== -1) return 'https://' + u;
    return u; // last-resort: hand back unmodified
  }
  function parseQuickLinks(text) {
    if (!text || typeof text !== 'string') return QUICK_LINKS_DEFAULT;
    const lines = text.split(/\r?\n/).map(s => s.trim()).filter(Boolean);
    const parsed = lines.map(line => {
      // Accept `Label | URL` (preferred) or `Label,URL` as a fallback.
      const sep = line.indexOf('|') !== -1 ? '|' : (line.indexOf(',') !== -1 ? ',' : null);
      if (!sep) return null;
      const idx = line.indexOf(sep);
      const label = line.slice(0, idx).trim();
      const url = normalizeUrl(line.slice(idx + 1));
      if (!label || !url) return null;
      return { label: label, url: url };
    }).filter(Boolean);
    return parsed.length ? parsed : QUICK_LINKS_DEFAULT;
  }
  // QUICK_LINKS is `let` because the inline editor mutates it after
  // the user saves changes via the front-end modal. The initial value
  // is parsed from the section setting (sensible defaults), then
  // replaced once the backend's stored value loads (overrides theme
  // setting if set).
  let QUICK_LINKS = parseQuickLinks((window.AG_POST_EDITOR_CONFIG || {}).quickLinks);
  let _quickLinksRawText = ''; // mirror of what's stored on the backend
  function renderQuickLinks() {
    const wrap = document.getElementById('ag-pe-quicklinks');
    if (!wrap) return;
    const chipsHtml = QUICK_LINKS.map((l, i) =>
      `<button type="button" class="ag-pe-quicklink" data-idx="${i}" title="Insertar enlace a ${escapeAttr(l.url)}">${escapeHtml(l.label)}</button>`
    ).join('');
    wrap.innerHTML = chipsHtml +
      `<button type="button" class="ag-pe-quicklink ag-pe-quicklink--edit" data-ag-pe-qlinks-edit title="Editar enlaces rápidos">⚙ Editar</button>`;
    wrap.querySelectorAll('.ag-pe-quicklink:not(.ag-pe-quicklink--edit)').forEach(btn => {
      btn.addEventListener('click', () => {
        const l = QUICK_LINKS[parseInt(btn.dataset.idx, 10)];
        insertQuickLink(l.url, l.label);
      });
    });
    wrap.querySelector('[data-ag-pe-qlinks-edit]').addEventListener('click', openQuickLinksEditor);
  }

  // Modal to edit the Quick Links inline. One textarea, format
  // `Label | URL` per line. Persists via the Apps Script
  // set_quick_links action, survives across browsers + sessions.
  function openQuickLinksEditor() {
    // Fall back to the current parsed list serialised back to text
    // if we haven't loaded the backend value yet.
    const seed = _quickLinksRawText || QUICK_LINKS.map(l => `${l.label} | ${l.url}`).join('\n');
    const overlay = document.createElement('div');
    overlay.className = 'ag-pe-author-overlay';
    overlay.innerHTML = `
      <div class="ag-pe-author-modal" role="dialog" style="max-width:560px">
        <header class="ag-pe-author-modal__head">
          <h3>Editar enlaces rápidos</h3>
          <button type="button" class="ag-pe-author-modal__close" aria-label="Cerrar">×</button>
        </header>
        <p class="ag-pe-author-modal__lede">Un chip por línea. Formato: <code>Etiqueta | URL</code>. Déjalo en blanco para restablecer los valores por defecto.</p>
        <textarea class="ag-pe-textarea" data-ag-pe-qlinks-text rows="12" style="margin:0 18px;width:calc(100% - 36px);font-family:'SF Mono',Menlo,monospace;font-size:13px"></textarea>
        <div style="display:flex;gap:8px;justify-content:flex-end;padding:14px 18px;border-top:1px solid var(--pe-border)">
          <button type="button" class="ag-pe-btn ag-pe-btn--ghost" data-ag-pe-qlinks-cancel>Cancelar</button>
          <button type="button" class="ag-pe-btn" data-ag-pe-qlinks-save style="background:#0c0c0c;color:#fff;border:1px solid #0c0c0c;padding:8px 18px;font-weight:600;cursor:pointer;border-radius:4px">Guardar</button>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);
    const ta = overlay.querySelector('[data-ag-pe-qlinks-text]');
    ta.value = seed;
    ta.focus();

    function close() { overlay.remove(); }
    overlay.querySelector('.ag-pe-author-modal__close').addEventListener('click', close);
    overlay.querySelector('[data-ag-pe-qlinks-cancel]').addEventListener('click', close);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

    overlay.querySelector('[data-ag-pe-qlinks-save]').addEventListener('click', async () => {
      const text = ta.value;
      const btn = overlay.querySelector('[data-ag-pe-qlinks-save]');
      btn.disabled = true;
      btn.innerHTML = '<span class="ag-pe-loading"></span>Guardando…';
      try {
        await api('set_quick_links', { text: text });
        _quickLinksRawText = text;
        QUICK_LINKS = parseQuickLinks(text);
        renderQuickLinks();
        toast('Enlaces rápidos actualizados', 'success');
        close();
      } catch (err) {
        toast('No se pudo guardar: ' + friendlyError(err), 'error');
        btn.disabled = false;
        btn.textContent = 'Guardar';
      }
    });
  }

  // Loads the backend-stored Quick Links text on editor render so
  // it overrides the section-setting fallback. Silent on error.
  async function loadQuickLinksFromBackend() {
    try {
      const r = await api('get_quick_links', {});
      if (r && typeof r.text === 'string' && r.text.trim()) {
        _quickLinksRawText = r.text;
        QUICK_LINKS = parseQuickLinks(r.text);
        renderQuickLinks();
      }
    } catch (e) { /* silent, keeps section-setting / default fallback */ }
  }

  // Insert a static link at the LAST KNOWN Quill cursor position
  // (clicking the chip steals focus from Quill, so getSelection() at
  // click-time returns null, we use state.lastRange, updated on
  // every Quill selection-change while the editor had focus).
  function insertQuickLink(url, label) {
    const q = state.quill;
    if (!q) return toast('Quill aún no está listo', 'error');
    const range = state.lastRange || { index: q.getLength(), length: 0 };
    if (range.length > 0) {
      // Wrap selection
      q.setSelection(range.index, range.length);
      q.format('link', url);
    } else {
      q.insertText(range.index, label, { link: url });
      q.setSelection(range.index + label.length, 0);
    }
    // Update tracked range so consecutive chip clicks insert sequentially
    state.lastRange = { index: range.index + (range.length || label.length), length: 0 };
    toast('Enlace insertado', 'success');
  }

  // Toggle the datetime input based on the status select
  function updateScheduleVisibility() {
    const status = document.getElementById('ag-pe-status');
    const wrap = document.getElementById('ag-pe-schedule-wrap');
    if (!status || !wrap) return;
    wrap.style.display = status.value === 'schedule' ? '' : 'none';
  }

  // Featured image preview
  function showFeaturedImage(url) {
    const preview = document.getElementById('ag-pe-image-preview');
    preview.src = url; preview.style.display = 'block';
    document.getElementById('ag-pe-image-hint').style.display = 'none';
    document.getElementById('ag-pe-image-clear').style.display = 'inline-block';
    document.getElementById('ag-pe-image-pick').textContent = 'Reemplazar imagen';
  }

  // Hydrate when editing
  async function hydrateForm(a) {
    document.getElementById('ag-pe-title').value = a.title || '';
    document.getElementById('ag-pe-summary').value = a.summary || '';
    {
      const status = articleStatus(a);
      const statusValue = status === 'scheduled' ? 'schedule' : (status === 'live' ? 'publish' : 'draft');
      document.getElementById('ag-pe-status').value = statusValue;
      if (status === 'scheduled' && a.publishedAt) {
        document.getElementById('ag-pe-schedule').value = isoToLocalDatetime(a.publishedAt);
      }
      updateScheduleVisibility();
    }
    document.getElementById('ag-pe-blog').value = a.blogId || '';
    document.getElementById('ag-pe-author').value = a.authorName || '';
    document.getElementById('ag-pe-handle').value = a.handle || '';
    document.getElementById('ag-pe-seo-title').value = a.seoTitle || '';
    document.getElementById('ag-pe-seo-desc').value = a.seoDescription || '';
    document.getElementById('ag-pe-image-alt').value = a.imageAltText || '';
    document.getElementById('ag-pe-post-type').value = a.postType || 'general';
    document.getElementById('ag-pe-takeaways').value = a.keyTakeaways || '';
    document.getElementById('ag-pe-author-bio').value = a.authorBio || '';
    document.getElementById('ag-pe-author-linkedin').value = a.authorLinkedin || '';
    if (a.authorAvatar) {
      state.authorAvatarUrl = a.authorAvatar;
      const preview = document.getElementById('ag-pe-avatar-preview');
      preview.src = a.authorAvatar; preview.style.display = 'block';
      document.getElementById('ag-pe-avatar-pick').textContent = 'Reemplazar avatar';
    }
    if (a.imageUrl) {
      state.featuredImage.url = a.imageUrl;
      state.featuredImage.altText = a.imageAltText || '';
      showFeaturedImage(a.imageUrl);
    }
    (a.tags || []).forEach(addTag);

    // Related products: fetch details by GID
    if (a.relatedProductGids && a.relatedProductGids.length) {
      try {
        const data = await api('list_products', { ids: a.relatedProductGids });
        state.selectedProducts = data.products || [];
        renderSelectedProducts();
      } catch (e) { /* non-fatal */ }
    }
    updateChecklist();
  }

  // ── Move post to another blog ───────────────────────────
  async function moveToBlog() {
    const select = document.getElementById('ag-pe-move-target');
    const targetId = select.value;
    if (!targetId) {
      toast('Elige un blog al que mover', 'error');
      return;
    }
    const targetTitle = select.options[select.selectedIndex].text;
    const ok = confirm(
      '¿Mover esta entrada a "' + targetTitle + '"?\n\n' +
      'Esto recreará la entrada en el nuevo blog y eliminará la original. ' +
      'La URL cambiará. La URL antigua dará un 404 (Shopify no redirige automáticamente).\n\n' +
      'Esto no se puede deshacer.'
    );
    if (!ok) return;

    const btn = document.getElementById('ag-pe-move');
    btn.disabled = true;
    btn.innerHTML = '<span class="ag-pe-loading"></span>Moviendo…';
    try {
      const result = await api('move_article', {
        article_id: state.currentArticle.id,
        target_blog_id: targetId
      }, 60000);
      toast('Movida: cargando la nueva entrada…', 'success');
      // Load the new article (which is now in the target blog)
      setTimeout(() => goEditor(result.article.id), 1000);
    } catch (err) {
      toast('Error al mover: ' + friendlyError(err), 'error');
      btn.disabled = false;
      btn.textContent = 'Mover';
    }
  }

  // ── Puente entre tiendas ────────────────────────────────
  // Envía la entrada actual a la tienda asociada (otro idioma /
  // marca). El backend asociado la traduce automáticamente y la crea
  // como BORRADOR para revisión, no se publica nada.
  async function pushToBridge() {
    if (!state.currentArticle) return;
    const label = bridgeLabel || 'la tienda asociada';
    const ok = confirm(
      label + '?\n\n' +
      'Esto envía la entrada a la otra tienda, donde se traduce automáticamente ' +
      'y se crea como BORRADOR para revisión. No se publica nada.\n\n' +
      '• Los productos relacionados NO se copian (catálogo distinto).\n' +
      '• Los enlaces a colecciones / productos pueden necesitar reajuste.\n' +
      '• La traducción automática necesita una revisión humana antes de publicar.'
    );
    if (!ok) return;

    const btn = document.getElementById('ag-pe-bridge');
    const original = btn ? btn.textContent : '';
    if (btn) { btn.disabled = true; btn.innerHTML = '<span class="ag-pe-loading"></span>Enviando…'; }
    try {
      const result = await api('bridge_push', { article_id: state.currentArticle.id }, 180000);
      let msg = 'Enviada a ' + (result.targetStore || 'la tienda asociada') + ' como borrador.';
      if (result.warning) msg += ' ⚠ ' + result.warning;
      toast(msg, result.warning ? 'error' : 'success');
      if (result.adminUrl) window.open(result.adminUrl, '_blank');
    } catch (err) {
      toast('Error al enviar: ' + friendlyError(err), 'error');
    } finally {
      if (btn) { btn.disabled = false; btn.textContent = original || bridgeLabel; }
    }
  }

  // ── Save ────────────────────────────────────────────────
  async function save(opts) {
    const forceDraft = !!(opts && opts.forceDraft);
    const data = {
      title: document.getElementById('ag-pe-title').value.trim(),
      summary: document.getElementById('ag-pe-summary').value.trim(),
      body: state.quill ? state.quill.root.innerHTML : '',
      blogId: document.getElementById('ag-pe-blog').value,
      authorName: document.getElementById('ag-pe-author').value.trim(),
      handle: document.getElementById('ag-pe-handle').value.trim(),
      ...(() => {
        if (forceDraft) return { isPublished: false, publishedAt: null };
        const status = document.getElementById('ag-pe-status').value;
        const scheduleVal = document.getElementById('ag-pe-schedule').value;
        if (status === 'draft') return { isPublished: false, publishedAt: null };
        if (status === 'schedule' && scheduleVal) {
          // Shopify GraphQL: setting isPublished: true with a future
          // publishedAt publishes NOW (the publishedAt is just metadata).
          // For an article to be SCHEDULED (hidden until the date),
          // it must be created with isPublished: false + future
          // publishedAt. Shopify's admin then shows "Scheduled" and
          // (in admin UI) flips isPublished automatically on the date.
          return { isPublished: false, publishedAt: localDatetimeToIso(scheduleVal) };
        }
        return { isPublished: true, publishedAt: new Date().toISOString() };
      })(),
      tags: getTags(),
      seoTitle: document.getElementById('ag-pe-seo-title').value.trim(),
      seoDescription: document.getElementById('ag-pe-seo-desc').value.trim(),
      imageUrl: state.featuredImage.url || '',
      imageAltText: document.getElementById('ag-pe-image-alt').value.trim(),
      postType: document.getElementById('ag-pe-post-type').value || 'general',
      keyTakeaways: document.getElementById('ag-pe-takeaways').value.trim(),
      authorBio: document.getElementById('ag-pe-author-bio').value.trim(),
      authorAvatar: state.authorAvatarUrl || '',
      authorLinkedin: document.getElementById('ag-pe-author-linkedin').value.trim(),
      relatedProductGids: state.selectedProducts.map(p => p.id)
    };

    if (!data.title) return toast('El título es obligatorio', 'error');
    if (!data.blogId) return toast('Elige un blog', 'error');
    if (!forceDraft
        && document.getElementById('ag-pe-status').value === 'schedule'
        && !document.getElementById('ag-pe-schedule').value) {
      return toast('Elige una fecha y hora para programar', 'error');
    }

    const btn = document.getElementById('ag-pe-save');
    const savedLabel = state.currentArticle ? 'Guardar cambios' : 'Crear entrada';
    btn.disabled = true;
    btn.innerHTML = '<span class="ag-pe-loading"></span>Guardando…';
    try {
      if (state.currentArticle) {
        await api('update_article', { article_id: state.currentArticle.id, data: data });
        toast('Guardado', 'success');
        btn.textContent = 'Guardar cambios';
      } else {
        const result = await api('create_article', { data: data });
        // Convert this session to "edit" mode so subsequent saves update
        // the same post rather than creating duplicates.
        state.currentArticle = {
          id: result.article.id,
          handle: result.article.handle || '',
          blogHandle: result.article.blogHandle || '',
          // Carry the just-submitted data so any in-editor render uses it,
          // not a stale read while Shopify finishes propagating metafields.
          title: data.title,
          summary: data.summary,
          body: data.body,
          tags: data.tags,
          isPublished: data.isPublished,
          authorName: data.authorName,
          imageUrl: data.imageUrl,
          imageAltText: data.imageAltText,
          seoTitle: data.seoTitle,
          seoDescription: data.seoDescription,
          postType: data.postType,
          keyTakeaways: data.keyTakeaways,
          authorBio: data.authorBio,
          authorAvatar: data.authorAvatar,
          authorLinkedin: data.authorLinkedin,
          relatedProductGids: data.relatedProductGids
        };
        toast('Creada: guardada. Haz clic en "← Todas las entradas" cuando termines.', 'success');
        btn.textContent = 'Guardar cambios';
      }
      // Editor stays put on save. Form retains what you just submitted,
      // no race with Shopify's metafield propagation. Tab back to list
      // when you're ready.
    } catch (err) {
      toast(friendlyError(err), 'error');
    } finally {
      btn.disabled = false;
      // Always restore the button label so "Saving…" doesn't get stuck on
      // error or timeout. After a successful save the success branch may
      // have already set it to "Save changes", overwrite either way.
      btn.textContent = state.currentArticle ? 'Guardar cambios' : savedLabel;
    }
  }

  // ── Tab routing ─────────────────────────────────────────
  function bindTabs() {
    root.querySelectorAll('.ag-pe-tab').forEach(t => {
      t.addEventListener('click', () => {
        const which = t.getAttribute('data-tab');
        if (which === 'list') goList();
        else if (which === 'new') goEditor(null);
      });
    });
  }

  // ── Storefront product search (fast, no Apps Script) ────
  async function fetchStorefrontProducts(query, limit) {
    if (query) {
      const url = '/search/suggest.json?q=' + encodeURIComponent(query)
        + '&resources[type]=product'
        + '&resources[limit]=' + (limit || 10)
        + '&resources[options][fields]=title,product_type,tag';
      const resp = await fetch(url, { headers: { 'Accept': 'application/json' } });
      const data = await resp.json();
      const list = (data && data.resources && data.resources.results && data.resources.results.products) || [];
      return list.map(p => ({
        id: 'gid://shopify/Product/' + p.id,
        title: p.title,
        imageUrl: p.image || '',
        handle: p.handle || ''
      }));
    }
    // No query, fetch most recent products as defaults
    const resp = await fetch('/products.json?limit=' + (limit || 8));
    const data = await resp.json();
    return (data.products || []).map(p => ({
      id: 'gid://shopify/Product/' + p.id,
      title: p.title,
      imageUrl: (p.images && p.images[0] && p.images[0].src) || '',
      handle: p.handle || ''
    }));
  }

  // ── Utilities ───────────────────────────────────────────
  function readFileAsBase64(file) {
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => {
        const result = r.result || '';
        const base64 = String(result).split(',')[1] || '';
        resolve(base64);
      };
      r.onerror = reject;
      r.readAsDataURL(file);
    });
  }

  // Shrinks image client-side before upload, typical phone photo
  // goes from ~5MB to ~300KB at 1600px / quality 0.85.
  // Returns a Blob (still .name / .type / .size compatible enough for our
  // upload path). Falls through to original file for SVG and small images.
  async function prepareImage(file) {
    const MAX_DIM = 1600;
    const QUALITY = 0.85;
    if (!file || !file.type || !file.type.startsWith('image/')) return file;
    if (file.type === 'image/svg+xml' || file.type === 'image/gif') return file;
    try {
      const bitmap = await createImageBitmap(file);
      const { width, height } = bitmap;
      const maxSide = Math.max(width, height);
      if (maxSide <= MAX_DIM && file.size < 800 * 1024) {
        bitmap.close && bitmap.close();
        return file;
      }
      const scale = maxSide > MAX_DIM ? MAX_DIM / maxSide : 1;
      const w = Math.round(width * scale);
      const h = Math.round(height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(bitmap, 0, 0, w, h);
      bitmap.close && bitmap.close();
      const targetType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
      return await new Promise((resolve) =>
        canvas.toBlob((blob) => {
          if (!blob) return resolve(file);
          // Preserve filename so the server-side has something sensible
          blob.name = file.name;
          resolve(blob);
        }, targetType, QUALITY)
      );
    } catch (e) {
      return file;
    }
  }
  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function escapeAttr(s) { return escapeHtml(s); }
  function formatDate(iso) {
    if (!iso) return 'Sin publicar';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch (e) { return ''; }
  }
  function slugify(s) {
    return String(s || '').toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-').replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }
  function articleStatus(a) {
    if (!a || !a.isPublished) return 'draft';
    if (a.publishedAt && new Date(a.publishedAt).getTime() > Date.now()) return 'scheduled';
    return 'live';
  }
  function articleStatusLabel(s) {
    return s === 'scheduled' ? 'Programada' : (s === 'live' ? 'En vivo' : 'Borrador');
  }
  // datetime-local input value (e.g. "2026-06-01T14:30") → ISO with TZ
  function localDatetimeToIso(local) {
    if (!local) return '';
    try { return new Date(local).toISOString(); } catch (e) { return ''; }
  }
  // ISO → datetime-local format (the input expects yyyy-MM-ddTHH:mm in local time)
  function isoToLocalDatetime(iso) {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      const pad = n => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch (e) { return ''; }
  }

  // ── Bootstrap ───────────────────────────────────────────
  if (!endpoint) {
    root.innerHTML = `<div class="ag-pe-error-banner">No hay ningún endpoint de backend configurado. Configúralo en el editor de temas (ajustes de la sección AG Post Editor).</div>`;
    return;
  }
  if (state.password) {
    // Try to skip the login screen
    api('list_blogs').then(data => {
      state.blogs = data.blogs || [];
      goList();
    }).catch(() => {
      state.password = '';
      sessionStorage.removeItem('agpe_password');
      renderLogin();
    });
  } else {
    renderLogin();
  }
})();
